import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmSkipCashOrder } from '../services/skipcash';
import { useCart } from '../context/CartContext';
import './ProcessingPayment.css';

const FAILED_STATUS_IDS = new Set([3, 4, 5, 6, 8]);

const parseStatusId = (value) => {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const getFailureMessage = (statusId, statusText) => {
  if (statusText) {
    return `Payment ${statusText.toLowerCase()}.`;
  }
  switch (statusId) {
    case 3:
      return 'Payment was canceled.';
    case 4:
      return 'Payment failed.';
    case 5:
      return 'Payment was rejected.';
    case 6:
      return 'Payment was refunded.';
    case 8:
      return 'Refund failed.';
    default:
      return 'Payment was not successful.';
  }
};

const mergeOrderInfo = (updates) => {
  let existing = null;
  try {
    const stored = sessionStorage.getItem('pendingOrder');
    existing = stored ? JSON.parse(stored) : null;
  } catch (error) {
    existing = null;
  }

  const next = {
    ...(existing || {}),
    ...updates,
  };

  if (!next.timestamp) {
    next.timestamp = Date.now();
  }

  sessionStorage.setItem('pendingOrder', JSON.stringify(next));
  if (next.orderId) {
    localStorage.setItem(`order_${next.orderId}`, JSON.stringify(next));
  }

  return next;
};

function ProcessingPayment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Preparing to verify your payment...');
  const [orderInfo, setOrderInfo] = useState({});
  const retryTimeoutRef = useRef(null);
  const isVerifyingRef = useRef(false);

  const verifyPayment = useCallback(async (info, explicitStatusId, explicitStatusText) => {
    if (isVerifyingRef.current) return;
    isVerifyingRef.current = true;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    try {
      if (!info.orderId && !info.receiptToken && !info.paymentId) {
        setStatus('error');
        setMessage('Payment information is missing. Please contact support.');
        return;
      }

      setStatus('processing');
      setMessage('Checking payment status with SkipCash...');

      const response = await confirmSkipCashOrder({
        receiptToken: info.receiptToken,
        orderId: info.orderId,
        paymentId: info.paymentId,
      });

      if (response?.status === 'created' || response?.status === 'exists') {
        const receiptToken = response?.order?.receiptToken || info.receiptToken;
        const resolvedOrderId = response?.order?.orderId || info.orderId;
        const mergedInfo = mergeOrderInfo({
          ...info,
          orderId: resolvedOrderId,
          receiptToken,
          orderDocId: response?.orderDocId,
        });

        setOrderInfo(mergedInfo);
        setStatus('success');
        setMessage('Payment confirmed. Redirecting to your receipt...');
        clearCart();

        const receiptTarget = receiptToken
          ? `/receipt?token=${encodeURIComponent(receiptToken)}`
          : resolvedOrderId
            ? `/receipt/${resolvedOrderId}`
            : '/';

        setTimeout(() => {
          navigate(receiptTarget, { replace: true });
        }, 1500);

        return;
      }

      const statusId = response?.statusId ?? explicitStatusId;
      const statusText = explicitStatusText;

      if (FAILED_STATUS_IDS.has(statusId)) {
        setStatus('failed');
        setMessage(getFailureMessage(statusId, statusText));
        return;
      }

      setStatus('pending');
      setMessage('Payment is still processing. We will check again shortly...');
      retryTimeoutRef.current = setTimeout(() => {
        verifyPayment(info, statusId, statusText);
      }, 5000);
    } catch (error) {
      console.error('Error verifying payment:', error);
      if (FAILED_STATUS_IDS.has(explicitStatusId)) {
        setStatus('failed');
        setMessage(getFailureMessage(explicitStatusId, explicitStatusText));
      } else {
        setStatus('error');
        setMessage('Unable to verify payment. Please try again or contact support.');
      }
    } finally {
      isVerifyingRef.current = false;
    }
  }, [navigate, clearCart]);

  useEffect(() => {
    const infoFromParams = {
      paymentId: searchParams.get('id') || searchParams.get('paymentId') || null,
      orderId: searchParams.get('transId') || searchParams.get('orderId') || searchParams.get('transactionId') || null,
      pendingOrderDocId: searchParams.get('custom1') || searchParams.get('pendingOrderDocId') || null,
      receiptToken: searchParams.get('token') || searchParams.get('receiptToken') || null,
    };
    const explicitStatusId = parseStatusId(searchParams.get('statusId'));
    const explicitStatusText = searchParams.get('status');

    const mergedInfo = mergeOrderInfo(infoFromParams);
    setOrderInfo(mergedInfo);

    if (!mergedInfo.orderId && !mergedInfo.receiptToken && !mergedInfo.paymentId) {
      setStatus('error');
      setMessage('Payment information is missing. Please contact support.');
      return undefined;
    }

    if (FAILED_STATUS_IDS.has(explicitStatusId)) {
      setStatus('failed');
      setMessage(getFailureMessage(explicitStatusId, explicitStatusText));
      return undefined;
    }

    verifyPayment(mergedInfo, explicitStatusId, explicitStatusText);

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [searchParams, verifyPayment]);

  const handleRetry = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    verifyPayment(orderInfo, null, null);
  };

  const renderIcon = () => {
    if (status === 'success') {
      return (
        <div className="processing-payment-icon success">
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
      );
    }

    if (status === 'failed' || status === 'error') {
      return (
        <div className="processing-payment-icon failed">
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
      );
    }

    return (
      <div className="processing-payment-icon">
        <div className="spinner"></div>
      </div>
    );
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return 'Payment Successful';
      case 'failed':
        return 'Payment Failed';
      case 'pending':
        return 'Payment Pending';
      case 'error':
        return 'Payment Error';
      default:
        return 'Processing Payment';
    }
  };

  return (
    <div className="processing-payment-page">
      <div className="processing-payment-container">
        <div className="processing-payment-content">
          {renderIcon()}
          <h1>{getTitle()}</h1>
          <p>{message}</p>
          {(orderInfo.orderId || orderInfo.paymentId) && (
            <p>
              {orderInfo.orderId && `Order: ${orderInfo.orderId}`}
              {orderInfo.orderId && orderInfo.paymentId && ' | '}
              {orderInfo.paymentId && `Payment ID: ${orderInfo.paymentId}`}
            </p>
          )}
          {status === 'pending' && (
            <p className="processing-payment-note">This may take a few moments...</p>
          )}
          {(status === 'pending' || status === 'error') && (
            <div className="processing-payment-actions">
              <button className="btn-primary" onClick={handleRetry}>
                Check Again
              </button>
              <button className="btn-outline" onClick={() => navigate('/')}>
                Back to Home
              </button>
            </div>
          )}
          {status === 'failed' && (
            <div className="processing-payment-actions">
              <button className="btn-primary" onClick={() => navigate('/checkout')}>
                Try Again
              </button>
              <button className="btn-outline" onClick={() => navigate('/shop')}>
                Continue Shopping
              </button>
              <button className="btn-outline" onClick={() => navigate('/')}>
                Back to Home
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProcessingPayment;
